import { FieldContext, ValidationError, ValidationResult } from "@angular/forms/signals";
import { SbAsins, SbCreativeType } from "@front/m19-api-client";
import { Catalog, ManualCollectionMaxAsins, ManualCollectionMinAsins, sbAsinsToList } from "@front/m19-models";

export function hasDuplicateSbAsins(groups: SbAsins[], creativeType: SbCreativeType): boolean {
  if (creativeType === SbCreativeType.manualCollection || creativeType === SbCreativeType.productCollection) {
    const seen = new Set<string>();
    for (const group of groups) {
      for (const asin of sbAsinsToList(group)) {
        if (seen.has(asin)) return true;
        seen.add(asin);
      }
    }
    return false;
  }

  const seen = new Set<string>();
  for (const group of groups) {
    const list = sbAsinsToList(group);
    if (list.length === 0) continue;
    const key = list.join("\0");
    if (seen.has(key)) return true;
    seen.add(key);
  }
  return false;
}

export function asinCountError(group: SbAsins, creativeType: SbCreativeType): ValidationError | null {
  const count = sbAsinsToList(group).length;
  if (creativeType === SbCreativeType.manualCollection || creativeType === SbCreativeType.productCollection) {
    return count < ManualCollectionMinAsins || count > ManualCollectionMaxAsins
      ? { kind: "incompleteGroup", message: "sb-form-asins.three_asins_required" }
      : null;
  }
  if (creativeType === SbCreativeType.storeSpotlight) {
    return count < 3 ? { kind: "incompleteGroup", message: "sb-form-asins.three_asins_required" } : null;
  }
  return count < 1 ? { kind: "required", message: "sb-form-asins.select_asins_for_your_creative" } : null;
}

export function duplicateErrorForGroup(
  groups: SbAsins[],
  index: number,
  creativeType: SbCreativeType,
): ValidationError | null {
  const group = groups[index];
  if (!group) return null;
  const list = sbAsinsToList(group);
  if (list.length === 0) return null;

  if (creativeType === SbCreativeType.manualCollection || creativeType === SbCreativeType.productCollection) {
    const counts = new Map<string, number>();
    for (const candidate of groups) {
      for (const asin of sbAsinsToList(candidate)) {
        counts.set(asin, (counts.get(asin) ?? 0) + 1);
      }
    }
    return list.some((asin) => (counts.get(asin) ?? 0) > 1)
      ? { kind: "duplicateSbAsins", message: "sb-form-asins.unique_line" }
      : null;
  }

  const key = list.join("\0");
  const occurrences = groups.filter((candidate) => sbAsinsToList(candidate).join("\0") === key).length;
  return occurrences > 1 ? { kind: "duplicateSbAsins", message: "sb-form-asins.unique_line" } : null;
}

export function adLineSharesSameParent(group: SbAsins, catalog: Catalog): boolean {
  const list = sbAsinsToList(group);
  if (list.length < 2) return false;
  const parents = list.map((asin) => catalog.getParentAsin(asin) ?? asin);
  return parents.every((parent) => parent === parents[0]);
}

export function sbAsinsErrorForGroup(
  groups: SbAsins[],
  index: number,
  creativeType: SbCreativeType,
): ValidationError | null {
  if (groups.length === 0) {
    return { kind: "required", message: "sb-form-asins.select_asins_for_your_creative" };
  }
  const group = groups[index];
  if (!group) return null;
  return asinCountError(group, creativeType) ?? duplicateErrorForGroup(groups, index, creativeType);
}

export function validateDistinctSbAsins(
  context: FieldContext<SbAsins[]>,
  creativeType: SbCreativeType,
): ValidationResult {
  return hasDuplicateSbAsins(context.value(), creativeType)
    ? { kind: "duplicateSbAsins", message: "sb-form-asins.unique_line" }
    : null;
}

export function validateSbAsins(context: FieldContext<SbAsins[]>, creativeType: SbCreativeType): ValidationResult {
  const groups = context.value();
  if (groups.length === 0) {
    return { kind: "required", message: "sb-form-asins.select_asins_for_your_creative" };
  }

  for (const group of groups) {
    const error = asinCountError(group, creativeType);
    if (error) return error;
  }
  return null;
}
